/**
 * Mirrors the SmsQueueStatus enum on the server.
 * @readonly
 * @enum {number}
 */
export const SmsQueueStatus = {
    AwaitingSend: 1,
    Sending: 2,
    Error: 5,
    Undelivered: 6,
    Cancelled: 7,
    Sent: 10,
    Delivered: 12,
};

/**
 * Mirrors the SmsCategory enum on the server.
 * @readonly
 * @enum {number}
 */
export const SmsCategory = {
    WaitlistOffer: 1,
    Billing: 2,
    DoorCode: 3,
    Broadcast: 4,
};

/**
 * Labels for each delivery state.
 *
 * "Sent" and "Delivered" are deliberately worded as different things. Twilio accepts almost every
 * message, so "Sent" only means it was accepted for delivery; "Delivered" is the carrier confirming
 * it reached the handset. Treating the two as the same is what hides a failing number.
 */
const STATUS_LABELS = {
    [SmsQueueStatus.AwaitingSend]: 'Waiting to send',
    [SmsQueueStatus.Sending]: 'Sending now',
    [SmsQueueStatus.Error]: 'Failed to send',
    [SmsQueueStatus.Undelivered]: 'Not delivered',
    [SmsQueueStatus.Cancelled]: 'Cancelled',
    [SmsQueueStatus.Sent]: 'Sent (awaiting receipt)',
    [SmsQueueStatus.Delivered]: 'Delivered',
};

const CATEGORY_LABELS = {
    [SmsCategory.WaitlistOffer]: 'Waitlist offer',
    [SmsCategory.Billing]: 'Billing',
    [SmsCategory.DoorCode]: 'Gate code',
    [SmsCategory.Broadcast]: 'Broadcast',
};

/**
 * The filter options offered above the log, in the order they are useful to an administrator.
 */
export const STATUS_FILTER_OPTIONS = [
    {value: '', label: 'All statuses'},
    {value: SmsQueueStatus.Delivered, label: STATUS_LABELS[SmsQueueStatus.Delivered]},
    {value: SmsQueueStatus.Sent, label: STATUS_LABELS[SmsQueueStatus.Sent]},
    {value: SmsQueueStatus.Undelivered, label: STATUS_LABELS[SmsQueueStatus.Undelivered]},
    {value: SmsQueueStatus.Error, label: STATUS_LABELS[SmsQueueStatus.Error]},
    {value: SmsQueueStatus.AwaitingSend, label: STATUS_LABELS[SmsQueueStatus.AwaitingSend]},
    {value: SmsQueueStatus.Sending, label: STATUS_LABELS[SmsQueueStatus.Sending]},
    {value: SmsQueueStatus.Cancelled, label: STATUS_LABELS[SmsQueueStatus.Cancelled]},
];

/**
 * @param status {number}
 * @returns {string}
 */
export function getSmsStatusLabel(status) {
    return STATUS_LABELS[status] ?? 'Unknown';
}

/**
 * The MUI severity colour for a delivery state.
 * @param status {number}
 * @returns {string}
 */
export function getSmsStatusColour(status) {
    switch (status) {
    case SmsQueueStatus.Delivered:
        return 'success';
    case SmsQueueStatus.Error:
    case SmsQueueStatus.Undelivered:
        return 'error';
    case SmsQueueStatus.Cancelled:
        return 'default';
    case SmsQueueStatus.AwaitingSend:
    case SmsQueueStatus.Sending:
        return 'info';
    default:
        return 'warning';
    }
}

/**
 * @param category {number}
 * @returns {string}
 */
export function getSmsCategoryLabel(category) {
    return CATEGORY_LABELS[category] ?? 'Other';
}

/**
 * Turns the inbound handler's action slug into something readable.
 *
 * The slugs come from SmsInboundActions on the server and are stored verbatim, so an unrecognised one
 * is shown as-is rather than hidden.
 * @param action {string}
 * @returns {string}
 */
export function getInboundActionLabel(action) {
    switch (action) {
    case 'received':
        return 'Received';
    case 'opted-out':
        return 'Opted out (STOP)';
    case 'opted-in':
        return 'Opted in (START)';
    case 'help-requested':
        return 'Asked for help';
    case 'waitlist-offer-accepted':
        return 'Accepted waitlist offer';
    case 'unknown-sender':
        return 'Unknown number';
    case 'not-understood':
        return 'Not understood';
    default:
        return action;
    }
}
